import {
  BadgeCheck,
  Check,
  Database,
  MailX,
  Quote,
  ShieldCheck,
} from 'lucide-react'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { GTM_CHECKOUT_AMOUNT } from '@laplace/contracts'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { GtmEstimatePanel } from '@/features/gtm/components/gtm-demo-results'
import { gtmMarker } from '@/features/gtm/components/gtm-marker'
import { focusGtmUrlInput } from '@/features/gtm/components/gtm-url-form'
import { buildGtmFunnelEstimate } from '@/features/gtm/domain/gtm-funnel-estimate'
import {
  GTM_EMAILS_PER_PERSON,
  GTM_PRICE_PER_EMAIL_USD,
} from '@/features/gtm/domain/gtm-pricing'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { cn } from '@/shared/lib/utils'

/**
 * Real customer quotes, shown only with the customer's permission. The
 * section stays hidden until there is at least one.
 */
const TESTIMONIALS: ReadonlyArray<{
  quote: string
  name: string
  role: string
  company: string
}> = []

const sectionPanel = 'rounded-2xl border border-sidebar-border bg-card/85 p-6'

export function GtmOwnIndex() {
  const { t } = useTranslation('gtm')
  const points = [t('indexPoint1'), t('indexPoint2'), t('indexPoint3')]

  return (
    <section className="py-6 sm:py-8">
      <div className="grid gap-5 rounded-2xl border border-sidebar-border bg-card/85 p-5 sm:gap-8 sm:p-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-center">
        <div>
          <span className="flex size-10 items-center justify-center rounded-xl bg-accent text-primary">
            <Database className="size-5" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-2xl font-display font-medium tracking-[-0.01em] text-foreground sm:text-3xl">
            <Trans
              t={t}
              i18nKey="indexTitle"
              components={{ accent: gtmMarker }}
            />
          </h2>
          <p className="mt-3 leading-7 text-muted-foreground">
            {t('indexText')}
          </p>
        </div>
        <ul className="divide-y divide-sidebar-border border-y border-sidebar-border">
          {points.map((point) => (
            <li
              key={point}
              className="flex items-start gap-3 py-3 text-foreground"
            >
              <Check
                className="mt-0.5 size-5 shrink-0 text-upgrade-cta"
                aria-hidden="true"
              />
              {point}
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}

export function GtmTrust() {
  const { t } = useTranslation('gtm')
  const items: Array<{ icon: LucideIcon; title: string; text: string }> = [
    { icon: Database, title: t('trust1Title'), text: t('trust1Text') },
    { icon: BadgeCheck, title: t('trust2Title'), text: t('trust2Text') },
    { icon: ShieldCheck, title: t('trust3Title'), text: t('trust3Text') },
    { icon: MailX, title: t('trust4Title'), text: t('trust4Text') },
  ]

  return (
    <section className="py-16">
      <h2 className="text-center text-3xl font-display font-medium tracking-[-0.01em] text-foreground">
        {t('trustTitle')}
      </h2>
      <ul className="mt-10 grid gap-4 sm:grid-cols-2">
        {items.map(({ icon: Icon, title, text }) => (
          <li
            key={title}
            className={cn(
              sectionPanel,
              'group transition-colors duration-300 hover:border-primary/50 motion-reduce:transition-none',
            )}
          >
            <div className="flex items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-secondary/60 text-primary transition-colors duration-300 group-hover:bg-accent motion-reduce:transition-none">
                <Icon className="size-4" aria-hidden="true" />
              </span>
              <div>
                <h3 className="pt-1.5 font-semibold text-foreground">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                  {text}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function GtmTestimonials() {
  const { t } = useTranslation('gtm')
  if (TESTIMONIALS.length === 0) return null

  return (
    <section className="py-16">
      <h2 className="text-center text-3xl font-display font-medium tracking-[-0.01em] text-foreground">
        {t('testimonialsTitle')}
      </h2>
      <ul className="mt-10 grid gap-4 sm:grid-cols-2">
        {TESTIMONIALS.map(({ quote, name, role, company }) => (
          <li key={`${company}-${name}`} className={sectionPanel}>
            <Quote className="size-5 text-upgrade-cta" aria-hidden="true" />
            <p className="mt-3 leading-7 text-foreground">{quote}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              {name} · {role}, {company}
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Pay as you go, as a calculator: the visitor picks a top-up within the
 * checkout's range and sees the emails, people and benchmark replies and
 * meetings it buys.
 */
export function GtmFinalCta() {
  const { t } = useTranslation('gtm')
  const lang = useCurrentLang()
  const number = new Intl.NumberFormat(lang)
  const [budgetUsd, setBudgetUsd] = useState<number>(
    GTM_CHECKOUT_AMOUNT.defaultUsd,
  )
  const estimate = buildGtmFunnelEstimate(budgetUsd)
  const { emails } = estimate.example
  const facts = [
    {
      label: t('pricingEmails', { budget: budgetUsd }),
      value: number.format(emails),
      marked: true,
    },
    {
      label: t('pricingPeople'),
      value: `${number.format(Math.floor(emails / GTM_EMAILS_PER_PERSON.max))}–${number.format(Math.floor(emails / GTM_EMAILS_PER_PERSON.min))}`,
      marked: false,
    },
    {
      label: t('pricingPerEmail'),
      value: t('pricingPerEmailValue', {
        value: number.format(GTM_PRICE_PER_EMAIL_USD),
      }),
      marked: false,
    },
  ]

  return (
    <section className="py-16 text-center">
      <h2 className="text-sm uppercase tracking-[0.18em] text-muted-foreground">
        {t('pricingTitle')}
      </h2>
      <div className="mx-auto mt-8 max-w-xl rounded-2xl border border-sidebar-border bg-card/85 p-4 text-left sm:p-6">
        <div data-markdown="skip">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-medium text-muted-foreground">
              {t('pricingTopUp')}
            </p>
            <p className="font-mono text-3xl font-medium tracking-[-0.03em] text-foreground tabular-nums">
              ${number.format(budgetUsd)}
            </p>
          </div>
          <Slider
            className="mt-3 h-11"
            min={GTM_CHECKOUT_AMOUNT.minUsd}
            max={GTM_CHECKOUT_AMOUNT.maxUsd}
            step={GTM_CHECKOUT_AMOUNT.stepUsd}
            value={[budgetUsd]}
            onValueChange={([value]) => setBudgetUsd(value)}
            thumbLabel={t('checkoutAmountLabel')}
          />
          <div className="flex justify-between font-mono text-xs text-muted-foreground tabular-nums">
            <span>${GTM_CHECKOUT_AMOUNT.minUsd}</span>
            <span>${number.format(GTM_CHECKOUT_AMOUNT.maxUsd)}</span>
          </div>
        </div>
        <dl className="mt-5 grid gap-2 sm:grid-cols-3 sm:gap-3">
          {facts.map((fact) => (
            <div
              key={fact.label}
              className={cn(
                'flex min-w-0 items-center justify-between gap-3 rounded-xl border p-3 sm:flex-col sm:items-start sm:gap-2',
                fact.marked
                  ? 'border-transparent bg-marker'
                  : 'border-sidebar-border bg-card/80',
              )}
            >
              <dt
                className={cn(
                  'min-w-0 text-xs leading-4',
                  fact.marked ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {fact.label}
              </dt>
              <dd className="shrink-0 font-mono text-xl font-semibold whitespace-nowrap text-foreground tabular-nums">
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="mx-auto mt-3 max-w-xl text-left">
        <GtmEstimatePanel estimate={estimate} />
      </div>
      <h2 className="mt-10 text-3xl font-display font-medium tracking-[-0.01em] text-foreground">
        {t('finalTitle')}
      </h2>
      <Button
        type="button"
        size="lg"
        className="mt-6 rounded-xl bg-upgrade-cta px-6 text-upgrade-cta-foreground hover:bg-upgrade-cta/90"
        onClick={focusGtmUrlInput}
      >
        {t('submit')}
      </Button>
    </section>
  )
}
